/**
 * Trip CO₂ Calculator — result chart (Recharts).
 *
 * Horizontal stacked bar chart over `ChartRow[]` from `buildComparison.ts`:
 *   • `<BarChart layout="vertical" />` flips the orientation so categories
 *     run top-to-bottom and bars extend rightwards.
 *   • Three stacked `<Bar>` series (electric → fuel → RFI) share a
 *     `stackId`. For non-flight rows two of those have `0`, so the bar
 *     renders as a single colour automatically — no special-casing.
 *   • Bar widths auto-scale to the row set's actual max kg (PRD §5.5
 *     "Option B"). The chosen row carries the conceptual 100% via the
 *     CHOSEN pill, not by capping the axis.
 *   • Custom `YAxis` tick via `<foreignObject>` so the row label can
 *     render the CHOSEN pill with normal CSS (impossible with raw SVG
 *     `<text>` without rebuilding box-model primitives).
 *   • Custom `LabelList` on the last stacked series (RFI) so the
 *     value at the right edge of the total bar formats as `%` or `kg`
 *     depending on the toggle. RFI's segment may be zero-width, but
 *     its right edge still aligns with the total stack's right edge.
 *
 * Phase 5 of the Trip CO₂ Calculator (PRD §5.5 / §5.6 / §5.7).
 *
 * Future Recharts levers we get for free by going this route:
 * Tooltip on hover (already wired), Brush for very long lists,
 * animation primitives, and per-series toggles via `Legend onClick`.
 */

import React, { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ChartRow } from './buildComparison';
import { MODE_DISPLAY, type TransportMode } from './modes';
import {
  GLOBAL_AVG_KG_PER_PERSON_PER_DAY,
  GLOBAL_AVG_KG_PER_PERSON_PER_YEAR,
} from './PerspectiveSection';
import InfoTip from './InfoTip';

// ─────────────────────────────────────────────────────────────────────────────
// Palette (PRD §5.5, locked)
// ─────────────────────────────────────────────────────────────────────────────

const C = {
  electric: 'hsl(174, 70%, 43%)',
  electricDk: 'hsl(174, 78%, 30%)',
  fuel: '#d97706',
  fuelDk: '#92400e',
  rfi: '#be123c',
  rfiDk: '#881337',
  trackBg: '#f1f5f9',
  trackBorder: '#e2e8f0',
  chosenBg: '#ecfdf5',
  chosenBorder: '#a7f3d0',
  chosenText: '#047857',
  text: '#0f172a',
  muted: '#64748b',
} as const;

// SVG-defs gradient ids. Module-scoped so the defs render once per page.
const GRAD = {
  electric: 'tripcalc-grad-electric',
  fuel: 'tripcalc-grad-fuel',
  rfi: 'tripcalc-grad-rfi',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Component shape: a Recharts datum is a flat object, so we project
// `ChartRow.breakdown.{electric,fuel,rfi}` up to top-level keys.
// ─────────────────────────────────────────────────────────────────────────────

interface Datum {
  id: ChartRow['id'];
  label: string;
  isChosen: boolean;
  kg: number;
  electric: number;
  fuel: number;
  rfi: number;
}

const toDatum = (row: ChartRow): Datum => ({
  id: row.id,
  label: row.label,
  isChosen: row.isChosen,
  kg: row.kg,
  electric: row.breakdown.electric,
  fuel: row.breakdown.fuel,
  rfi: row.breakdown.rfi,
});

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export interface ResultChartProps {
  rows: ChartRow[];
}

type Unit = 'percent' | 'kg';

// Bar height + per-row padding. `BARSIZE` × rows.length + a margin floor
// gives the chart a height that's always enough — Recharts needs an
// explicit number, not "shrink-to-fit".
const BARSIZE = 18;
const ROW_HEIGHT = 36;
const CHART_MARGIN = { top: 6, right: 56, left: 0, bottom: 6 };
const Y_AXIS_WIDTH = 168;

const ResultChart: React.FC<ResultChartProps> = ({ rows }) => {
  const [unit, setUnit] = useState<Unit>('percent');

  const data = useMemo(() => rows.map(toDatum), [rows]);
  const chosenKg = useMemo(
    () => data.find((d) => d.isChosen)?.kg ?? Math.max(1, ...data.map((d) => d.kg)),
    [data]
  );

  if (rows.length === 0) return null;

  return (
    <section
      aria-label="Comparison chart"
      style={{
        // Surface tokens kept in sync with ComparisonLeadIn,
        // PerspectiveSection, and CantTrainDialog so the four sections
        // between the map and the share row read as one visual family.
        // No marginTop: the parent `.trip-calc-results-row` owns the
        // vertical rhythm — both the gap above the row and the gap
        // between the two cards inside it (mobile stacks, desktop
        // grids). Setting marginTop here would inline-override the
        // wrapper's CSS and break the equal-height alignment.
        padding: '1.25rem',
        background: '#fafafa',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '0.5rem',
          gap: '0.5rem',
          flexWrap: 'wrap',
        }}
      >
        <h2 style={{ fontSize: '1.1rem', margin: 0 }}>Climate impact per transport type</h2>
        <UnitToggle unit={unit} onChange={setUnit} />
      </div>

      <ResponsiveContainer width="100%" height={data.length * ROW_HEIGHT + 24}>
        <BarChart layout="vertical" data={data} margin={CHART_MARGIN} barSize={BARSIZE}>
          <defs>
            <linearGradient id={GRAD.electric} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={C.electric} />
              <stop offset="100%" stopColor={C.electricDk} />
            </linearGradient>
            <linearGradient id={GRAD.fuel} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={C.fuel} />
              <stop offset="100%" stopColor={C.fuelDk} />
            </linearGradient>
            <linearGradient id={GRAD.rfi} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={C.rfi} />
              <stop offset="100%" stopColor={C.rfiDk} />
            </linearGradient>
          </defs>

          <XAxis type="number" hide domain={[0, 'dataMax']} />
          <YAxis
            type="category"
            dataKey="label"
            width={Y_AXIS_WIDTH}
            tickLine={false}
            axisLine={false}
            interval={0}
            tick={(props) => <CategoryTick {...props} data={data} />}
          />

          <Tooltip
            cursor={{ fill: C.trackBg, opacity: 0.5 }}
            content={(props) => (
              <BreakdownTooltip {...props} unit={unit} chosenKg={chosenKg} />
            )}
          />

          <Bar dataKey="electric" stackId="emissions" fill={`url(#${GRAD.electric})`} />
          <Bar dataKey="fuel" stackId="emissions" fill={`url(#${GRAD.fuel})`} />
          <Bar dataKey="rfi" stackId="emissions" fill={`url(#${GRAD.rfi})`}>
            {/* Total-stack value label at the right edge of the bar.
                Attached to the LAST stacked series so the position is
                always at the total's end (RFI = 0 still aligns there). */}
            <LabelList
              dataKey="kg"
              position="right"
              content={(props) => (
                <ValueLabel {...props} unit={unit} chosenKg={chosenKg} data={data} />
              )}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <Legend />
    </section>
  );
};

export default ResultChart;

// ─────────────────────────────────────────────────────────────────────────────
// Unit toggle (% / CO₂e)
// ─────────────────────────────────────────────────────────────────────────────

interface UnitToggleProps {
  unit: Unit;
  onChange: (next: Unit) => void;
}

const UnitToggle: React.FC<UnitToggleProps> = ({ unit, onChange }) => {
  const Btn: React.FC<{ value: Unit; label: string }> = ({ value, label }) => {
    const active = unit === value;
    return (
      <button
        type="button"
        onClick={() => onChange(value)}
        aria-pressed={active}
        style={{
          padding: '4px 10px',
          fontSize: 12,
          fontWeight: 600,
          background: active ? C.text : 'transparent',
          color: active ? '#fff' : C.muted,
          border: `1px solid ${active ? C.text : C.trackBorder}`,
          borderRadius: 999,
          cursor: 'pointer',
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div role="group" aria-label="Display units" style={{ display: 'inline-flex', gap: 6 }}>
      <Btn value="percent" label="%" />
      <Btn value="kg" label="CO₂e" />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Custom YAxis tick — label + CHOSEN pill via foreignObject (HTML in SVG)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Recharts wraps `tick` as an element and forwards layout props at
 * runtime. We use `unknown` + a focused pick to keep the public API
 * narrow without leaking Recharts' internal types.
 */
interface CategoryTickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
  data: Datum[];
}

const CategoryTick: React.FC<CategoryTickProps> = ({ x = 0, y = 0, payload, data }) => {
  const datum = payload ? data.find((d) => d.label === payload.value) : undefined;
  const fontSize = 13;
  const pillFontSize = 10;
  const w = Y_AXIS_WIDTH - 8;
  const h = 28;
  return (
    <foreignObject x={x - w} y={y - h / 2} width={w} height={h}>
      <div
        // foreignObject children need an xmlns to render correctly across
        // browsers when the SVG is later serialised (e.g. for OG images).
        // For live React rendering both forms work; explicit is safer.
        // @ts-expect-error xmlns is not in HTMLAttributes but is valid here.
        xmlns="http://www.w3.org/1999/xhtml"
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 6,
          padding: datum?.isChosen ? '0 6px' : '0',
          background: datum?.isChosen ? C.chosenBg : 'transparent',
          border: `1px solid ${datum?.isChosen ? C.chosenBorder : 'transparent'}`,
          borderRadius: 6,
          fontSize,
          fontWeight: 600,
          color: C.text,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {payload?.value ?? ''}
        </span>
        {datum?.isChosen && (
          <span
            style={{
              fontSize: pillFontSize,
              fontWeight: 700,
              letterSpacing: 0.5,
              color: C.chosenText,
              background: C.chosenBorder,
              padding: '2px 6px',
              borderRadius: 999,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            chosen
          </span>
        )}
      </div>
    </foreignObject>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Custom value label at the right edge of the total stack
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Recharts injects geometry props (`x`, `y`, `width`, `height`) typed
 * as `string | number | undefined` because some chart shapes use SVG
 * percentage strings. We only ever get numbers here (Bar geometry is
 * always numeric), so we coerce defensively with `Number()`.
 */
interface ValueLabelProps {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  height?: number | string;
  index?: number;
  unit: Unit;
  chosenKg: number;
  data: Datum[];
}

const ValueLabel: React.FC<ValueLabelProps> = ({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  index,
  unit,
  chosenKg,
  data,
}) => {
  const datum = typeof index === 'number' ? data[index] : undefined;
  if (!datum) return null;

  const xn = Number(x);
  const yn = Number(y);
  const wn = Number(width);
  const hn = Number(height);

  const text =
    unit === 'percent'
      ? `${Math.round((datum.kg / chosenKg) * 100).toLocaleString()}%`
      : `${Math.round(datum.kg).toLocaleString()} kg`;

  return (
    <text
      x={xn + wn + 6}
      y={yn + hn / 2}
      dy={4}
      fontSize={12}
      fontWeight={700}
      fill={C.text}
    >
      {text}
    </text>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Tooltip — breakdown by emission source
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Recharts' Tooltip `content` prop forwards `payload` as `readonly any[]`
 * (it can hold many series shapes). We only render when the first
 * entry is a `Datum`, and we tell TypeScript that with a runtime check
 * + a focused cast — narrower than typing the prop as `any`.
 */
interface TooltipProps {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: unknown }>;
  unit: Unit;
  chosenKg: number;
}

const BreakdownTooltip: React.FC<TooltipProps> = ({ active, payload, unit, chosenKg }) => {
  if (!active || !payload || payload.length === 0) return null;
  const raw = payload[0]?.payload;
  if (!raw || typeof raw !== 'object' || !('label' in raw)) return null;
  const d = raw as Datum;
  const fmt = (v: number) =>
    unit === 'percent'
      ? `${Math.round((v / chosenKg) * 100).toLocaleString()}%`
      : `${Math.round(v).toLocaleString()} kg`;
  const Line: React.FC<{ color: string; label: string; value: number }> = ({
    color,
    label,
    value,
  }) =>
    value <= 0 ? null : (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
        <span
          style={{ width: 10, height: 10, background: color, borderRadius: 2, display: 'inline-block' }}
          aria-hidden
        />
        <span style={{ color: C.muted }}>{label}</span>
        <span style={{ marginLeft: 'auto', fontWeight: 700 }}>{fmt(value)}</span>
      </div>
    );
  return (
    <div
      style={{
        background: '#fff',
        padding: '8px 10px',
        border: `1px solid ${C.trackBorder}`,
        borderRadius: 6,
        boxShadow: '0 6px 20px rgba(15,23,42,0.08)',
        minWidth: 180,
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{d.label}</div>
      <div style={{ display: 'grid', gap: 4 }}>
        <Line color={C.electric} label="Electricity" value={d.electric} />
        <Line color={C.fuel} label="Fuel" value={d.fuel} />
        <Line color={C.rfi} label="Radiative forcing" value={d.rfi} />
        <div
          style={{
            borderTop: `1px solid ${C.trackBorder}`,
            paddingTop: 4,
            marginTop: 2,
            display: 'flex',
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          <span style={{ color: C.muted }}>Total</span>
          <span style={{ marginLeft: 'auto' }}>{fmt(d.kg)}</span>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Legend (custom — RFI swatch carries a tooltip per PRD §5.7)
// ─────────────────────────────────────────────────────────────────────────────

const Legend: React.FC = () => {
  const Swatch: React.FC<{ from: string; to: string; label: string; help?: React.ReactNode }> = ({
    from,
    to,
    label,
    help,
  }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span
        aria-hidden
        style={{
          width: 12,
          height: 12,
          borderRadius: 3,
          background: `linear-gradient(90deg, ${from}, ${to})`,
          display: 'inline-block',
        }}
      />
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label}
        {help && <InfoTip content={help} position="top" ariaLabel={`About: ${label}`} />}
      </span>
    </span>
  );

  return (
    <div
      style={{
        marginTop: '0.5rem',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.75rem',
        fontSize: 12,
        color: C.muted,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Swatch from={C.electric} to={C.electricDk} label="CO₂ from electricity" />
      <Swatch from={C.fuel} to={C.fuelDk} label="CO₂ from fuel" />
      <Swatch
        from={C.rfi}
        to={C.rfiDk}
        label="Radiative forcing (flights)"
        help={
          <>
            At cruise altitude, contrails and NOx can almost triple a flight's climate
            impact beyond its CO₂ alone (Lee et al. 2021). Multipliers used: 1.5×
            short-haul, 2.0× medium, 2.7× long.
          </>
        }
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Methodology panel
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Detour multipliers — mirrors `backend/services/carbonAccounting/detour.js`.
 * Kept inline here because the methodology panel is the only consumer and
 * these values change very rarely; extract to a shared module if a second
 * caller emerges. If the backend ever drifts, the mirror is wrong — the
 * source of truth is the backend file.
 */
const DETOUR_MULTIPLIERS: Readonly<Record<string, number>> = Object.freeze({
  airplane: 1.0,
  ferry: 1.1,
  train: 1.2,
  bus: 1.3,
  car: 1.3,
  van: 1.3,
  motorcycle: 1.3,
});

/**
 * Static fallback formulas for supported modes that aren't always
 * present in the chart's row set. The chart's default row set is
 * `train / bus / car / plane`; van, motorcycle, and ferry only get a
 * row when the user picks them. Per PRD §5.6 ("methodology lists ALL
 * supported emission factors, not only the ones used in the current
 * calculation"), the methodology panel still needs to surface their
 * formula so a user who picked car can see what a motorcycle or
 * ferry trip would have looked like without re-running.
 *
 * Sentence shape mirrors the backend's `buildComprehensiveExplanation`
 * (`backend/services/carbonAccounting/explanations.js`):
 *   detour clause → factor + source → occupancy / RF note.
 *
 * Factor values mirror `backend/services/carbonAccounting/factors.js`
 * — keep in sync if the backend changes (no caller fetches this from
 * the API today, on purpose: a static fallback that's a few months
 * stale is still useful, whereas a fetch failure hides the formula
 * entirely).
 */
/**
 * Cross-variant factor lists for the modes that *are* in the chart's
 * default set (train, bus, car, plane). The dynamic per-row explanation
 * only describes the variant the user picked — but the PRD §5.6 spec
 * requires the methodology panel to surface ALL supported factors, so
 * a user who picked petrol-car can see what diesel/electric would have
 * looked like without re-running. Rendered as a short "All variants:"
 * aside underneath each dynamic bullet.
 *
 * Values mirror `backend/services/carbonAccounting/factors.js` and the
 * methodology framework v3 §2. Keep in sync if the backend changes.
 */
const ALL_VARIANTS: Readonly<Partial<Record<TransportMode, string>>> = Object.freeze({
  car:
    'Per vehicle-km: petrol 211 g, diesel 195 g, hybrid 130 g, electric 82 g (EU-27 grid average). ' +
    'All four are DEFRA 2024 EU WLTP averages for a compact car. The result for any fuel is then ' +
    'divided by the passenger count you set (1–5).',
  train:
    'Per passenger-km: electric (default) 24 g on the EU-27 grid mix (Travel Carbon Label, ECF 2021); ' +
    'diesel 91 g on non-electrified routes (Umweltbundesamt, Germany 2022). Nordic-grid trains are ' +
    'closer to 7 g but we use the EU-27 average to stay conservative.',
  bus:
    'Per passenger-km: long-distance coach (default) 42 g, urban / local bus 85 g. Both DEFRA 2024. ' +
    'Occupancy is already averaged into the factor (~17–20 passengers for coach, ~9 for urban).',
  airplane:
    'Economy, per passenger-km, by distance: short (<1,000 km) 255 g × 1.5 RF = 383 g effective; ' +
    'medium (1,000–3,700 km) 195 g × 2.0 = 390 g; long (>3,700 km) 195 g × 2.7 = 527 g. Business class ' +
    'is roughly 1.6× economy. Sources: DEFRA 2024 (fuel CO₂) + Lee et al. 2021 (radiative forcing).',
});

const FALLBACK_EXPLANATIONS: Readonly<Partial<Record<TransportMode, string>>> = Object.freeze({
  van:
    'Straight-line distance × 1.3 detour factor (roads detour around terrain and settlements). ' +
    'Multiplied by 317 g CO₂e/km (DEFRA 2024, EU light commercial van diesel) and divided ' +
    'by the number of passengers you set (1–7). No radiative forcing applies for ground transport.',
  motorcycle:
    'Straight-line distance × 1.3 detour factor (roads detour around terrain and settlements). ' +
    'Multiplied by 60 g CO₂e/km, the per-vehicle factor for a petrol motorcycle under 250 cc ' +
    '(ADEME Base Carbone (2021)), and divided by the rider count (1 or 2). Petrol is the only ' +
    'fuel modelled today; electric motorcycles are rare enough in the EU fleet that ADEME has ' +
    'no published factor we can cite. No radiative forcing applies for ground transport.',
  ferry:
    'Straight-line distance × 1.1 detour factor (shipping lanes detour around coastlines). ' +
    'Multiplied by the per-passenger factor: 19 g CO₂e/km (foot passenger), 113 g CO₂e/km ' +
    '(average mix, the calculator default), or 130 g CO₂e/km (car passenger, includes allocated ' +
    'vehicle emissions), all DEFRA 2024. The published factors already average occupancy ' +
    'across typical ferry loading. DEFRA derives these using the EN 16258 standard, which lets ' +
    'operators allocate ship emissions by mass or by area; different ferry companies pick ' +
    'different methods, so a single trip’s "true" per-passenger number can swing roughly ' +
    '±20% from our default. No radiative forcing applies for marine transport.',
});

export interface MethodologyPanelProps {
  rows: ChartRow[];
}

/**
 * Body of the "Assumptions" methodology block. Exported so the
 * standalone MethodologyDialog (tabbed) can reuse the same copy as
 * MethodologyPanel without duplication.
 */
export const AssumptionsContent: React.FC = () => (
  <ul style={{ paddingLeft: '1.1rem', margin: 0 }}>
    <li>
      Defaults: <b>electric trains</b> (EU grid average), <b>long-distance coach buses</b>,{' '}
      <b>economy-class flights</b>. Cars, vans, and motorcycles use the passenger count
      and fuel you set in the inputs.
    </li>
    <li style={{ marginTop: 4 }}>
      Distance is the <b>great-circle (haversine) line</b> between the two city coordinates
      returned by the geocoder, then multiplied by a mode-specific detour factor to estimate
      the real route: <DetourSummary />.
    </li>
    <li style={{ marginTop: 4 }}>
      <b>Per-passenger vs per-vehicle.</b> Train, bus, plane, and ferry factors are already
      per passenger (typical occupancy is baked in). Car, van, and motorcycle factors are
      per vehicle, so we divide by the number of people you entered to surface a per-passenger
      number.
    </li>
    <li style={{ marginTop: 4 }}>
      <b>Round-trip</b> results are doubled, assuming the return leg uses the same mode
      and config as the outbound.
    </li>
    <li style={{ marginTop: 4 }}>
      <b>Geographic scope.</b> All factors are EU/UK averages. They are most accurate inside
      the EU. Outside the EU, plane, car, van, motorcycle, and ferry numbers stay reasonable
      (these factors are global-fleet averages); train and bus numbers can drift, because
      they depend on the country's electricity grid and bus occupancy norms.
    </li>
    <li style={{ marginTop: 4 }}>
      <b>Data vintage.</b> DEFRA 2024 for road, ferry, bus, and aviation CO₂; ECF Travel
      Carbon Label (2021) for electric rail; Umweltbundesamt (2022) for diesel rail;
      ADEME Base Carbone (2021) for motorcycle; Lee et al. 2021 for aviation radiative
      forcing.
    </li>
    <li style={{ marginTop: 4 }}>
      <b>Not included.</b> Vehicle manufacturing, battery production, rail-track and
      airport infrastructure, country-specific electricity grids (we use the EU-27 average
      for EVs), aircraft type, airline, weather, and per-flight contrail probability.
      Numbers are directional, not per-flight or per-operator precise.
    </li>
  </ul>
);

/**
 * Body of the "Per-mode calculation" methodology block. Dynamic bullets
 * come from the user's `rows`; static fallbacks fill in any supported
 * mode not present in the row set (PRD §5.6 — methodology lists ALL
 * supported factors, not just the chosen one).
 */
export const PerModeCalculationContent: React.FC<{ rows: ChartRow[] }> = ({ rows }) => (
  <ul style={{ paddingLeft: '1.1rem', margin: 0 }}>
    {/*
      Dynamic bullets: one per row actually shown on the chart, with
      real numbers from the user's trip. These come from the backend's
      comprehensive explanation (factor + detour + RF + result).
      When a mode has multiple variants (car fuels, train types, bus
      types, plane distance categories), append an "All variants:"
      aside so the user sees the alternatives without re-running.
    */}
    {rows
      .filter((r) => r.explanation)
      .map((r) => (
        <li key={r.id} style={{ marginTop: 4 }}>
          <b>{r.label}</b>: {r.explanation}
          {ALL_VARIANTS[r.primaryMode] && r.kind === 'mode' && (
            <div style={{ marginTop: 2, fontStyle: 'italic' }}>
              All variants: {ALL_VARIANTS[r.primaryMode]}
            </div>
          )}
        </li>
      ))}
    {/*
      Static fallback bullets: any supported mode missing from the
      chart still gets its formula listed. The chart's default set
      is train/bus/car/plane, so van/motorcycle/ferry only appear
      here when the user picked one of them.
    */}
    {(['van', 'motorcycle', 'ferry'] as const)
      .filter((mode) => !rows.some((r) => r.kind === 'mode' && r.id === mode))
      .map((mode) => (
        <li key={`fallback-${mode}`} style={{ marginTop: 4 }}>
          <b>
            {MODE_DISPLAY[mode].emoji} {MODE_DISPLAY[mode].label}
          </b>
          : {FALLBACK_EXPLANATIONS[mode]}
        </li>
      ))}
  </ul>
);

/**
 * Body of the "Days of an average person" methodology block. Constants
 * (~13.6 kg/day, ~4,970 kg/year) come from PerspectiveSection so the
 * two pages stay in sync.
 */
export const DaysOfAverageComparisonContent: React.FC = () => (
  <>
    <p style={{ margin: 0 }}>
      <b>Formula:</b> trip emissions (kg CO₂e per passenger) ÷ {GLOBAL_AVG_KG_PER_PERSON_PER_DAY.toFixed(1)} kg
      per day, rounded to the nearest whole day. Anything that would round below 1 is shown
      as 1 day, because a sub-day number reads as "negligible," and that's not what most
      short trips are.
    </p>

    <ul style={{ paddingLeft: '1.1rem', margin: '0.5rem 0 0' }}>
      <li>
        <b>Why ~{GLOBAL_AVG_KG_PER_PERSON_PER_DAY.toFixed(1)} kg/day?</b> Global per-capita
        emissions were ~{GLOBAL_AVG_KG_PER_PERSON_PER_YEAR.toLocaleString()} kg CO₂e/year in
        2022 (Our World in Data, drawing on the Global Carbon Project, FAO, and IEA). Divided
        by 365 days, that's ~{GLOBAL_AVG_KG_PER_PERSON_PER_DAY.toFixed(1)} kg per person per day.
      </li>
      <li style={{ marginTop: 4 }}>
        <b>Why a global baseline, not an EU one?</b> Using the EU average (~7,000 kg/year)
        would make every EU traveller look "better than average" by construction:
        geographic snobbery dressed up as methodology. The global number is the honest
        comparator.
      </li>
      <li style={{ marginTop: 4 }}>
        <b>What's in the ~{GLOBAL_AVG_KG_PER_PERSON_PER_YEAR.toLocaleString()} kg figure?</b>{' '}
        The full per-capita lifestyle footprint (energy at home, food, goods, services, and
        transport), summed across the global population. So this comparison is "one travel
        decision versus a full day of average life," not "travel versus travel."
      </li>
      <li style={{ marginTop: 4 }}>
        <b>What it does <i>not</i> mean.</b> It isn't <i>your</i> daily footprint; yours
        depends on your country, income, diet, and how much you fly. It's the world-average
        human's. Use it as a unit of intuition, not as a benchmark to beat.
      </li>
      <li style={{ marginTop: 4 }}>
        <b>Source.</b> Our World in Data, "Per-capita CO₂ emissions" (2022 cohort).
      </li>
    </ul>
  </>
);

/**
 * Inline methodology panel — a single `<details>` block with the three
 * methodology sections collapsed inside. Kept for backwards-compat with
 * any caller that still wants the inline (non-dialog) presentation; the
 * Trip Calculator page itself now mounts `MethodologyDialog` instead so
 * the long methodology copy doesn't push the conversion CTA off-screen.
 */
export const MethodologyPanel: React.FC<MethodologyPanelProps> = ({ rows }) => (
  <details
    style={{
      marginTop: '1rem',
      padding: '0.5rem 0.75rem',
      background: '#fafafa',
      border: `1px solid ${C.trackBorder}`,
      borderRadius: 6,
    }}
  >
    <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: C.text }}>
      How is this calculated?
    </summary>

    <div style={{ marginTop: '0.5rem', fontSize: 12.5, color: C.muted, lineHeight: 1.55 }}>
      <Subsection title="Assumptions">
        <AssumptionsContent />
      </Subsection>

      <CollapsibleSubsection title="Per-mode calculation" style={{ marginTop: '0.75rem' }}>
        <PerModeCalculationContent rows={rows} />
      </CollapsibleSubsection>

      <CollapsibleSubsection
        title='"Days of an average person" comparison'
        style={{ marginTop: '0.75rem' }}
      >
        <DaysOfAverageComparisonContent />
      </CollapsibleSubsection>
    </div>
  </details>
);

const SUBSECTION_TITLE_STYLE: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: C.text,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 4,
};

const Subsection: React.FC<{
  title: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ title, children, style }) => (
  <div style={style}>
    <div style={SUBSECTION_TITLE_STYLE}>{title}</div>
    {children}
  </div>
);

/**
 * Same visual identity as `Subsection`, but the body is folded behind
 * a native `<details>` element. Used for the methodology panel's
 * "Assumptions" block, which is metadata most users won't read but
 * an auditor will want one click away.
 *
 * The title is styled to match `SUBSECTION_TITLE_STYLE` so the
 * collapsed state still looks like a section header, not a generic
 * disclosure widget. `marginBottom: 0` overrides the default so the
 * gap below the summary matches the gap above the next subsection.
 */
const CollapsibleSubsection: React.FC<{
  title: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ title, children, style }) => (
  <details style={style}>
    <summary
      style={{
        ...SUBSECTION_TITLE_STYLE,
        marginBottom: 0,
        cursor: 'pointer',
        // Keep the disclosure triangle aligned visually with the
        // uppercase label; default <summary> rendering already handles
        // the marker, no need to restyle it.
      }}
    >
      {title}
    </summary>
    <div style={{ marginTop: 6 }}>{children}</div>
  </details>
);

const DetourSummary: React.FC = () => {
  // Group modes by their multiplier so we render "bus/car/van/motorcycle ×1.3"
  // once instead of four times.
  const byMultiplier = new Map<number, string[]>();
  for (const [mode, m] of Object.entries(DETOUR_MULTIPLIERS)) {
    const display = mode === 'airplane' ? 'plane' : mode;
    const arr = byMultiplier.get(m) ?? [];
    arr.push(display);
    byMultiplier.set(m, arr);
  }
  const groups = Array.from(byMultiplier.entries()).sort(([a], [b]) => a - b);
  return (
    <>
      {groups.map(([m, modes], i) => (
        <React.Fragment key={m}>
          {i > 0 && '; '}
          {modes.join('/')} ×{m}
        </React.Fragment>
      ))}
    </>
  );
};
